import { CommonModule, NgClass } from '@angular/common';
import {
  Component,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { BpCoreModule } from '@bp-core/src/lib/bp-core.module';
import { IconModule } from '@bp-core/src/lib/external/iconify-angular/projects/iconify/src/public-api';
import { ConfigModel, UserModel } from '@bp-core/src/lib/proto/common-message.pb';
import { PortalUserGrpcServiceClient } from '@bp-core/src/lib/proto/portal-user.pbsc';
import { UserGrpcServiceClient } from '@bp-core/src/lib/proto/user.pbsc';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';
import { RedirectService } from '@bp-core/src/lib/services/portal/redirect.service';
import { environment } from '@environment/environment';
import icSearch from '@iconify/icons-ic/twotone-search';

import { MatDialogRef } from '@angular/material/dialog';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { GrpcCoreModule } from '@ngx-grpc/core';
import { Empty } from '@ngx-grpc/well-known-types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { BpUserComponent } from 'bp/layout/common/bp-user/bp-user.component';
import { BpConfirmationModalComponent } from 'bp/shared/components/bp-confirmation-modal/bp-confirmation-modal.component';
import { BpHeaderConfig } from 'bp/shared/models/header-config';
import { BpConfirmationService } from 'bp/shared/services/bp-confirmation.service';
import { BpHeaderService } from 'bp/shared/services/bp-header.service';
import { SudoAccessStatus } from 'bp/shared/access/sudo-access.types';
import { filterSudoNavigation, withOwnerActive } from 'bp/shared/access/sudo-navigation.filter';
import { revalidateSudoAccess } from 'bp/shared/guards/sudo-access.guard';
import { SudoAccessStore } from 'bp/shared/services/sudo-access.store';
import { Observable, Subject, filter, take, takeUntil } from 'rxjs';
import { SearchComponent } from '../../../../../app/layout/common/search/search.component';
import { AiChatWidgetComponent } from '../../../../../app/layout/common/ai-chat-widget/ai-chat-widget.component';

@Component({
  selector: 'bp-layout',
  templateUrl: './bp.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    FuseVerticalNavigationComponent,
    FuseLoadingBarComponent,
    MatButtonModule,
    RouterOutlet,
    MatIconModule,
    BpUserComponent,
    NgClass,
    GrpcCoreModule,
    IconModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    SearchComponent,
    AiChatWidgetComponent,
    BpCoreModule,
  ],
})
export class BpLayoutComponent implements OnInit, OnDestroy {
  env = environment;
  isScreenSmall: boolean;
  /** Menú completo del mock de la API; lo que se pinta sale de filtrarlo con los permisos (feature 057). */
  private readonly fullNavigation = signal<Navigation | undefined>(undefined);
  /** URL actual: el menú enciende el ítem dueño de un detalle (BET-987, `withOwnerActive`). */
  private readonly currentUrl = signal('');
  readonly sudoAccess = inject(SudoAccessStore);
  private readonly injector = inject(Injector);
  /** Seis filas fantasma mientras llegan los permisos (artboard 2.b). */
  readonly skeletonRows = Array.from({ length: 6 });
  /**
   * `true` cuando los permisos llegaron Y ya se revalidó la URL actual: recién ahí se monta el contenido,
   * para que una página sin acceso no llegue a abrirse ni un instante.
   */
  readonly accessChecked = signal(false);
  /** El menú no se pinta hasta tener los permisos: nadie ve ítems que después desaparecen. */
  private readonly filteredNavigation = computed<Navigation | undefined>(() => {
    const navigation = this.fullNavigation();
    if (!navigation) return undefined;
    const items = this.sudoAccess.ready()
      ? filterSudoNavigation(navigation.bp ?? [], this.sudoAccess.ctx())
      : [];
    return { ...navigation, bp: items };
  });
  /**
   * Con el ítem dueño de la URL encendido. Se separa del filtro para que cambiar de página no rehaga el arreglo
   * del menú: `withOwnerActive` devuelve el mismo si no hay nada que marcar y Fuse no vuelve a pintar ni plegar.
   */
  readonly navigation = computed<Navigation | undefined>(() => {
    const filtered = this.filteredNavigation();
    if (!filtered) return undefined;
    const items = withOwnerActive(filtered.bp ?? [], this.currentUrl());
    return items === filtered.bp ? filtered : { ...filtered, bp: items };
  });
  user: UserModel;
  fileUrlBase = environment.urlApiV2 + 'api/Files/';
  config: ConfigModel;
  token = localStorage.getItem('access_token');
  headerConfig: BpHeaderConfig | null = null;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  routerUrl: string;
  searchControl = new FormControl();
  icSearch = icSearch;
  @ViewChild('searchModal') searchModal: TemplateRef<any>;
  searchDialogRef: MatDialogRef<BpConfirmationModalComponent, any>;
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      this.openSearchDialog();
    }
  }

  /**
   * Constructor
   */
  constructor(
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private authService: AuthService,
    private userGrpcServiceClient: UserGrpcServiceClient,
    private redirectService: RedirectService,
    private portalUserGrpcServiceClient: PortalUserGrpcServiceClient,
    private bpConfirmationService: BpConfirmationService,
    private router: Router,
    private bpHeaderService: BpHeaderService,
  ) {
    bpHeaderService.headerConfig$.subscribe(config => {
      setTimeout(() => {
        this.headerConfig = config;
      }, 0);
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // this.getDollarBoxIsAvalibleConfig();

    this.routerUrl = this.router.url;
    this.currentUrl.set(this.routerUrl);

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(val => {
      this.routerUrl = (val as NavigationEnd).url;
      this.currentUrl.set(this.routerUrl);
    });

    this.authService.userSubject$.pipe(takeUntil(this._unsubscribeAll)).subscribe(user => {
      if (user) this.user = user;
    });

    // La configuración se conserva para sus otros usos; el menú ya no depende de ella (R-12).
    this.portalUserGrpcServiceClient.getConfig(new Empty()).subscribe({
      next: config => (this.config = config),
      error: error => console.error('getConfig', error),
    });

    this._navigationService
      .get()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(navigation => this.fullNavigation.set(navigation));

    this.waitForAccess();

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });
  }
  /**
   * Espera los permisos y, cuando llegan, revalida la URL actual (la primera navegación se dejó pasar
   * mientras cargaban) antes de montar el contenido. Si fallan, el aviso de error ofrece reintentar.
   */
  private waitForAccess(access$: Observable<SudoAccessStatus> = this.sudoAccess.load()): void {
    this.accessChecked.set(false);
    access$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(async status => {
      if (status !== 'ready') return;
      await runInInjectionContext(this.injector, () => revalidateSudoAccess());
      this.accessChecked.set(true);
    });
  }

  /** Botón «Reintentar» del aviso de error de permisos. */
  retryAccess(): void {
    this.waitForAccess(this.sudoAccess.retry());
  }

  openSearchDialog() {
    this.searchDialogRef = this.bpConfirmationService.open({
      // content: SearchComponent
      // Sin título: el de bp-core pone «Confirmación» si no se pasa uno (070, T092).
      title: '',
      bodyTemplate: this.searchModal,
      width: '400px',
      noPadding: true,
      actions: {
        confirm: {
          show: false,
        },
      },
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  openIntercomChat($event: MouseEvent) {
    $event.preventDefault();
    try {
      (window as any).Intercom('show');
    } catch (error) {
      console.log(error);
    }
  }

  bpImgClick() {
    this.router.navigate(['/dashboard']);
  }

  formatFaIcon(faIcon: string) {
    let faIconClass = '';
    const faSplitIcon = faIcon.split('-');
    switch (faSplitIcon[0]) {
      case 'fab fa':
        faIconClass = 'bp-fa-icon ' + faIcon;
        break;
      case 'fa':
        faIconClass = 'bp-fa-icon fa ' + faIcon;
        break;
    }
    return faIconClass;
  }

  // Get icon or icon class of a goal for the nav
  getIconOrClass(portalIcon?: string): { icon: string; classes: string } {
    if (!portalIcon) return { icon: 'mat_outline:help_outline', classes: '' };

    const fuseIcon = {
      'fa-baby': 'mat_outline:escalator_warning',
      'fa-user-graduate': 'heroicons_outline:academic-cap',
      'fa-piggy-bank': 'mat_outline:savings',
      'fa-gift': 'heroicons_outline:gift',
      'fa-home': 'heroicons_outline:home',
      'fa-umbrella': 'feather:umbrella',
      'fa-user-tie': 'heroicons_outline:briefcase',
      'fa-heart': 'heroicons_outline:heart',
      'fa-arrow-up': 'feather:arrow-up-circle',
      'fa-umbrella-beach': 'mat_solid:beach_access',
      'fa-arrow-circle-up': 'feather:arrow-up-circle',
      'fa-users': 'heroicons_outline:user-group',
      'fa-biking': 'mat_outline:directions_bike',
      'fa-plane-departure': 'mat_outline:flight_takeoff',
      'fa-graduation-cap': 'heroicons_outline:academic-cap',
      'fa-mountain': 'mat_outline:landscape',
      'fa-university': 'heroicons_outline:building-library',
    };

    if (Object.keys(fuseIcon).includes(portalIcon)) {
      return { icon: fuseIcon[portalIcon], classes: '' };
    } else {
      // Icon always has to have content
      // this icon is goin to has d-none
      return {
        icon: 'heroicons_solid:chevron-up',
        classes: this.formatFaIcon(portalIcon) || 'fa-regular fa-circle',
      };
    }
  }
}
